import {
  EventType,
  PublicClientApplication,
  type AccountInfo,
  type Configuration,
  type EventMessage,
} from '@azure/msal-browser'
import type { AppConfig } from './config'

/**
 * MSAL against the Environment's External ID tenant.
 *
 * Every value is configuration and arrives at runtime, not at build time: Development and
 * Production are different directories, and a client id compiled in would tie one artefact to
 * one Environment. `loadConfig()` has therefore run before anything here is called.
 *
 * MFA is enforced on the application in the tenant rather than here. A client that decided for
 * itself whether to demand a second factor could be told not to.
 */
export function msalConfiguration(config: AppConfig): Configuration {
  return {
    auth: {
      clientId: config.entraClientId,
      authority: config.entraAuthority,
      // An external tenant's authority is not a *.microsoftonline.com host, so MSAL will not
      // trust it on its own. Naming it here is what stops sign-in failing as an unknown
      // authority before the browser ever leaves the page.
      knownAuthorities: [config.entraAuthorityHost],
      // The tenant holds the origin itself -- `http://localhost:5174` locally. Sending anything
      // longer, a path included, is AADSTS50011 rather than a sign-in.
      redirectUri: window.location.origin,
      postLogoutRedirectUri: window.location.origin,
    },
    cache: {
      // Session, not local: an Admin session is privileged and ends with the tab.
      cacheLocation: 'sessionStorage',
    },
  }
}

/**
 * The one scope the API accepts. The roles ride in the token as claims rather than as scopes --
 * Owner, Author, Checker and Support -- and the API is what enforces them.
 */
export function apiScopes(config: AppConfig): string[] {
  return [config.apiScope]
}

/**
 * The app roles an access token carries.
 *
 * THE ACCESS TOKEN IS THE ONLY ONE THAT HAS THEM. Owner, Author, Checker and Support are app
 * roles on the API's registration, not on this SPA's, so the id token this app is issued
 * carries no `roles` claim at all and reading that one showed a full Owner as nobody.
 *
 * Read them only to say what this sign-in is worth, or to hide what the API would refuse
 * anyway. The hiding is a courtesy; the gate is the API, which reads this same claim off this
 * same token -- a client that decided for itself what it was allowed to do could be told
 * otherwise.
 *
 * The token is opaque to a client by convention rather than by encoding: the middle segment is
 * the claims, base64url, and nothing here trusts what it finds beyond putting it on a screen.
 * Anything unreadable is no roles, because a menu is not the place to report a malformed
 * token.
 */
export function rolesIn(accessToken: string | null | undefined): readonly string[] {
  const segment = accessToken?.split('.')[1]
  if (!segment) {
    return []
  }

  try {
    const base64 = segment.replace(/-/g, '+').replace(/_/g, '/')
    const claimed = JSON.parse(atob(base64.padEnd(Math.ceil(base64.length / 4) * 4, '='))).roles

    return Array.isArray(claimed) ? claimed : []
  } catch {
    return []
  }
}

/** What to greet the signed-in Admin with. The tenant does not always carry a display name. */
export function nameOf(account: AccountInfo | null | undefined): string {
  return account?.name ?? account?.username ?? 'Signed in'
}

/**
 * The instance the whole app shares.
 *
 * `MsalProvider` calls `initialize()` and `handleRedirectPromise()` itself, so neither is done
 * here. What is done here is the active account: MSAL knows the accounts it has cached, but not
 * which one requests should use, and `acquireTokenSilent` without one throws rather than
 * choosing. Setting it on the login event covers the account arriving back from a redirect, and
 * the line before covers a reload that found one already in sessionStorage.
 */
export function createMsal(config: AppConfig): PublicClientApplication {
  const instance = new PublicClientApplication(msalConfiguration(config))

  // NOTHING IS ASKED OF THE INSTANCE HERE. `MsalProvider` calls `initialize()`, and until it
  // has, reading the cache throws `uninitialized_public_client_application` -- which, thrown
  // in a render, is a blank page rather than an error. It only ever fired on the second visit,
  // when there was a cached account to read, so a first sign-in looked entirely well.
  //
  // Nothing is lost by waiting: `MsalProvider` restores the cached accounts as it initialises,
  // the callback below adopts the one that signs in, and the API client falls back to the
  // first cached account when no active one has been chosen (api/client.ts).
  instance.addEventCallback((message: EventMessage) => {
    if (message.eventType !== EventType.LOGIN_SUCCESS) return
    const payload = message.payload as { account?: AccountInfo } | null
    if (payload?.account) instance.setActiveAccount(payload.account)
  })

  return instance
}

/**
 * Whether the URL still carries the tenant's answer to a sign-in.
 *
 * MSAL comes back to the origin with its code -- or its error -- in the fragment, and reads it
 * a tick after the app first renders. Anything that rewrites the URL before then throws the
 * answer away, and a sign-in that the tenant granted ends silently back on the prompt with
 * nothing logged. Ask this before redirecting out of the front door.
 */
export function isAuthResponse(hash: string = window.location.hash): boolean {
  const fragment = new URLSearchParams(hash.replace(/^#/, ''))
  return fragment.has('code') || fragment.has('error') || fragment.has('id_token')
}
